import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

type AppRole = 'owner' | 'admin' | 'staff' | null;

// Role priority: owner > admin > staff
const ROLE_PRIORITY: Record<string, number> = {
  owner: 3,
  admin: 2,
  staff: 1,
};

interface UseUserRoleResult {
  role: AppRole;
  roles: AppRole[];
  isLoading: boolean;
  error: Error | null;
}

export const useUserRole = (): UseUserRoleResult => {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async (): Promise<{ role: AppRole; roles: AppRole[] }> => {
      if (!user) return { role: null, roles: [] };

      // Get ALL roles for the user (they may have multiple)
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) {
        console.error("Error fetching user roles:", error);
        return { role: null, roles: [] };
      }

      if (!data || data.length === 0) {
        return { role: null, roles: [] };
      }

      // Get all roles
      const roles = data.map(d => d.role as AppRole);
      
      // Return the highest priority role
      const highestRole = roles.reduce((highest, current) => {
        if (!highest) return current;
        if (!current) return highest;
        return (ROLE_PRIORITY[current] || 0) > (ROLE_PRIORITY[highest] || 0) ? current : highest;
      }, null as AppRole);

      return { role: highestRole, roles };
    },
    enabled: !!user,
    staleTime: 60000, // Cache for 1 minute
  });

  return {
    role: data?.role ?? null,
    roles: data?.roles ?? [],
    isLoading,
    error: error as Error | null,
  };
};
